/**
 * @file ReportIssueModal.tsx
 * @brief In-app form for logging product bugs and beta feedback.
 */

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Divider,
  Chip,
  Tabs,
  Tab,
  Alert,
} from '@mui/material';
import { Bug, Lightbulb, HelpCircle, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { appFeedbackService, type AppFeedbackType, type AppFeedbackSeverity } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { useSnackbar } from '@/shared/contexts/SnackbarContext';
import { AppSelect, AppMenuItem } from '@/shared/components/AppSelect';

interface ReportIssueModalProps {
  open: boolean;
  onClose: () => void;
}

const TYPE_OPTIONS: { value: AppFeedbackType; label: string; helper: string }[] = [
  { value: 'bug', label: 'Bug', helper: 'Something in the app is broken or wrong' },
  { value: 'feedback', label: 'Feedback', helper: 'Suggestion to improve the app' },
  { value: 'question', label: 'Question', helper: 'Need help using the app' },
];

const SEVERITY_OPTIONS: AppFeedbackSeverity[] = ['low', 'medium', 'high', 'critical'];

const statusColor = (status: string) => {
  if (status === 'resolved' || status === 'closed') return 'success';
  if (status === 'in_progress') return 'warning';
  return 'default';
};

const ReportIssueModal: React.FC<ReportIssueModalProps> = ({ open, onClose }) => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useSnackbar();
  const [tab, setTab] = useState(0);
  const [type, setType] = useState<AppFeedbackType>('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<AppFeedbackSeverity>('medium');

  const pagePath = `${location.pathname}${location.search || ''}`;

  useEffect(() => {
    if (open) {
      setTab(0);
      setType('bug');
      setTitle('');
      setDescription('');
      setSeverity('medium');
    }
  }, [open]);

  const { data: myReports = [], isLoading: mineLoading } = useQuery({
    queryKey: QUERY_KEYS.appFeedbackMine,
    queryFn: () => appFeedbackService.getMine(),
    enabled: open && tab === 1,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      appFeedbackService.create({
        type,
        title: title.trim(),
        description: description.trim(),
        severity: type === 'bug' ? severity : undefined,
        pagePath,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.appFeedbackMine });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.appFeedbackAll });
      showSuccess('Thanks. Your report was sent to the team.');
      onClose();
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ||
        (error as Error)?.message ||
        'Failed to submit report';
      showError(message);
    },
  });

  const canSubmit = title.trim().length > 0 && description.trim().length > 0 && !createMutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Bug size={20} />
            <Typography variant="h6" fontWeight={600}>
              Report an issue
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <X size={18} />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2 }}>
        <Tab label="New report" />
        <Tab label="My reports" />
      </Tabs>
      <Divider />

      {tab === 0 ? (
        <>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Alert severity="info" sx={{ py: 0.5 }}>
                Use this for problems in NestTask itself — not for work-task issues.
              </Alert>

              <AppSelect
                label="What are you reporting?"
                value={type}
                onChange={(e) => setType(e.target.value as AppFeedbackType)}
                fullWidth
                size="small"
              >
                {TYPE_OPTIONS.map((opt) => (
                  <AppMenuItem key={opt.value} value={opt.value}>
                    {opt.label} — {opt.helper}
                  </AppMenuItem>
                ))}
              </AppSelect>

              {type === 'bug' && (
                <AppSelect
                  label="Severity"
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value as AppFeedbackSeverity)}
                  fullWidth
                  size="small"
                >
                  {SEVERITY_OPTIONS.map((opt) => (
                    <AppMenuItem key={opt} value={opt}>
                      {opt.charAt(0).toUpperCase() + opt.slice(1)}
                    </AppMenuItem>
                  ))}
                </AppSelect>
              )}

              <TextField
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                fullWidth
                size="small"
                inputProps={{ maxLength: 200 }}
                placeholder={type === 'bug' ? 'e.g. Cannot add a subtask on Docking' : 'Short summary'}
              />

              <TextField
                label={type === 'bug' ? 'What happened?' : 'Details'}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                fullWidth
                multiline
                minRows={4}
                placeholder={
                  type === 'bug'
                    ? 'What you did, what you expected, and what actually happened.'
                    : 'Tell us more so we can follow up.'
                }
              />

              <Typography variant="caption" color="text.secondary">
                Current page: {pagePath || '/'}
              </Typography>
            </Box>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={onClose} variant="outlined" color="inherit">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => createMutation.mutate()}
              disabled={!canSubmit}
            >
              {createMutation.isPending ? 'Sending...' : 'Submit report'}
            </Button>
          </DialogActions>
        </>
      ) : (
        <DialogContent>
          {mineLoading ? (
            <Typography variant="body2" color="text.secondary">
              Loading...
            </Typography>
          ) : myReports.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              You have not submitted any reports yet.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {myReports.map((item) => (
                <Box
                  key={item.id}
                  sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    {item.type === 'bug' ? (
                      <Bug size={14} />
                    ) : item.type === 'feedback' ? (
                      <Lightbulb size={14} />
                    ) : (
                      <HelpCircle size={14} />
                    )}
                    <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                      {item.title}
                    </Typography>
                    <Chip size="small" label={item.status.replace('_', ' ')} color={statusColor(item.status)} />
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {dayjs(item.createdAt).format('D MMM YYYY, h:mm A')}
                    {item.pagePath ? ` · ${item.pagePath}` : ''}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: 'pre-wrap' }}>
                    {item.description}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      )}
    </Dialog>
  );
};

export default ReportIssueModal;
